<template>
    <div class="EvieChat">
        <EvieMobileToolbar
            :has-conversations="hasPastConversations"
            :is-sidebar-open="isMobileSidebarOpen"
            :conversation-title="activeConversationTitle"
            @toggle="toggleMobileSidebar"
            @rename="onToolbarRename"
        />
        <div
            v-if="isMobileSidebarOpen && hasPastConversations"
            class="EvieChat__mobileBackdrop"
            @click="isMobileSidebarOpen = false"
        />
        <EvieSidebar
            v-if="hasPastConversations"
            :conversation-summaries="conversationSummaries"
            :active-conversation-id="activeConversationId"
            :streaming-conversation-ids="streamingConversationIds"
            :is-mobile-open="isMobileSidebarOpen"
            :is-mobile-animating="isMobileSidebarAnimating"
            :rp-name="rpName"
            @new-chat="onSidebarNewChat"
            @select-conversation="onSidebarSelectConversation"
        />
        <div class="EvieChat__main">
            <EvieChatThinking
                v-if="chatState === ChatState.Loading && !hasPastConversations"
                class="EvieChat__loader"
                :visible="true"
            />
            <EvieChatLanding
                v-else-if="chatState === ChatState.Landing"
                @new-chat="$emit('new-chat')"
                @send="$emit('send', $event)"
            />
            <EvieChatEmptyConversation
                v-else-if="chatState === ChatState.Composing"
                :input-draft="inputDraft"
                @send="$emit('send', $event)"
                @draft-change="$emit('draft-change', $event)"
            />
            <EvieChatConversationPanel
                v-else
                :is-loading="chatState === ChatState.Loading"
                :active-conversation="activeConversation"
                :send-status="sendStatus"
                :input-draft="inputDraft"
                :streaming-message="streamingMessage"
                @retry="$emit('retry', $event)"
                @new-chat="$emit('new-chat')"
                @send="$emit('send', $event)"
                @cancel="$emit('cancel')"
                @draft-change="$emit('draft-change', $event)"
                @feedback="$emit('feedback', $event)"
            />
        </div>
    </div>
</template>

<style lang="scss">
.EvieChat {
    display: flex;
    height: 100%;
    overflow: hidden;

    &__main {
        flex: 1;
        min-width: 0;
        display: grid;
        grid-template-columns: 1fr;
        grid-template-rows: 1fr auto;
        overflow: hidden;
    }

    &__loader {
        display: flex;
        align-items: center;
        justify-content: center;
        height: 100%;
    }

    &__mobileBackdrop {
        display: none;

        @media (max-width: 1024px) {
            display: block;
            position: fixed;
            inset: 0;
            background: rgba(0, 0, 0, 0.4);
            z-index: 499;
        }
    }

    @media (max-width: 1024px) {
        flex-direction: column;
    }
}
</style>

<script lang="ts">
    import { defineComponent, PropType } from 'vue';

    import { EvieConversationSummary } from '@evidentid/tprm-portal-lib/models/evie/EvieConversationSummary.model';

    import { ChatState } from '@/modules/evie-ai/models/ChatState.model';
    import { EvieConversation } from '@/modules/evie-ai/models/EvieConversation.model';
    import { OPTIMISTIC_CONVERSATION_ID } from '@/modules/evie-ai/store';

    import EvieChatEmptyConversation from '../EvieChatEmptyConversation/EvieChatEmptyConversation.vue';
    import EvieChatLanding from '../EvieChatLanding/EvieChatLanding.vue';
    import EvieChatThinking from '../EvieChatThinking/EvieChatThinking.vue';
    import EvieMobileToolbar from '../EvieMobileToolbar/EvieMobileToolbar.vue';
    import EvieSidebar from '../EvieSidebar/EvieSidebar.vue';

    import EvieChatConversationPanel from './sub-components/EvieChatConversationPanel.vue';

    export default defineComponent({
        name: 'EvieChat',
        components: {
            EvieSidebar,
            EvieMobileToolbar,
            EvieChatLanding,
            EvieChatThinking,
            EvieChatEmptyConversation,
            EvieChatConversationPanel,
        },
        props: {
            conversationSummaries: {
                type: Object as PropType<Record<string, EvieConversationSummary>>,
                required: true,
            },
            activeConversation: {
                type: Object as PropType<EvieConversation | null>,
                default: null,
            },
            activeConversationId: { type: String as PropType<string | null>, default: null },
            streamingConversationIds: { type: Array as PropType<string[]>, default: (): string[] => [] },
            sendStatus: { type: String as PropType<'idle' | 'loading'>, required: true },
            inputDraft: { type: String, required: true },
            isComposing: { type: Boolean, default: false },
            isLoading: { type: Boolean, default: false },
            rpName: { type: String, required: true },
            streamingMessage: { type: String as PropType<string | null>, default: null },
        },
        emits: [ 'cancel', 'draft-change', 'feedback', 'new-chat', 'retry', 'select-conversation', 'send' ],
        data() {
            return {
                ChatState,
                isMobileSidebarOpen: false,
                isMobileSidebarAnimating: false,
            };
        },
        computed: {
            activeConversationTitle(): string {
                const id = this.activeConversationId;
                return (id && this.conversationSummaries[id]?.title) || '';
            },
            hasPastConversations(): boolean {
                return Object.keys(this.conversationSummaries).length > 0
                    || this.activeConversationId === OPTIMISTIC_CONVERSATION_ID
                    || Boolean(this.activeConversation?.messageIds.length);
            },
            chatState(): ChatState {
                if (this.isLoading) {
                    return ChatState.Loading;
                }
                if (this.activeConversation && this.activeConversation.messageIds.length > 0) {
                    return ChatState.Active;
                }
                if (this.isComposing || this.hasPastConversations) {
                    return ChatState.Composing;
                }
                return ChatState.Landing;
            },
        },
        created() {
            (this as any)._animationTimerId = null as ReturnType<typeof setTimeout> | null;
        },
        beforeUnmount() {
            if ((this as any)._animationTimerId !== null) {
                clearTimeout((this as any)._animationTimerId);
            }
        },
        methods: {
            toggleMobileSidebar() {
                if ((this as any)._animationTimerId !== null) {
                    clearTimeout((this as any)._animationTimerId);
                }
                this.isMobileSidebarAnimating = true;
                this.isMobileSidebarOpen = !this.isMobileSidebarOpen;
                // 250ms matches the EvieSidebar transition duration (EvieSidebar.vue).
                (this as any)._animationTimerId = setTimeout(() => {
                    this.isMobileSidebarAnimating = false;
                    (this as any)._animationTimerId = null;
                }, 250);
            },
            onSidebarNewChat() {
                this.isMobileSidebarOpen = false;
                this.$emit('new-chat');
            },
            onSidebarSelectConversation(conversationId: string) {
                this.isMobileSidebarOpen = false;
                this.$emit('select-conversation', conversationId);
            },
            onToolbarRename() {
                const id = this.activeConversationId;
                if (id) {
                    (this as any).$procedures.execute('evieRenameConversationProcedure', {
                        rpName: this.rpName,
                        conversationId: id,
                        currentTitle: this.activeConversationTitle,
                    });
                }
            },
        },
    });
</script>
